import {
  Timestamp,
  collection,
  getDocs,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';

export type ScheduledTestStatus = 'scheduled' | 'kit_sent' | 'in_progress' | 'completed' | 'cancelled';

export type ScheduledTestCategory =
  | 'blood_panel'
  | 'genetic'
  | 'imaging'
  | 'wearable_setup'
  | 'follow_up'
  | 'other';

/**
 * Lives at users/{uid}/scheduled_tests/{testId}.
 * Ops writes these when a clinician plans a test off the back of a quarterly review
 * or an add-on request. The member portal reads them to show "Upcoming tests".
 */
export interface ScheduledTestRecord {
  id: string;
  name: string;
  category: ScheduledTestCategory;
  status: ScheduledTestStatus;
  scheduledFor?: Timestamp | null;
  provider?: string;
  location?: string;
  instructions?: string;
  notes?: string;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

const OPEN_STATUSES: ScheduledTestStatus[] = ['scheduled', 'kit_sent', 'in_progress'];

function mapScheduledTest(id: string, data: Record<string, any>): ScheduledTestRecord {
  return {
    id,
    name: data.name || 'Untitled test',
    category: (data.category as ScheduledTestCategory) || 'other',
    status: (data.status as ScheduledTestStatus) || 'scheduled',
    scheduledFor: data.scheduledFor ?? null,
    provider: data.provider || undefined,
    location: data.location || undefined,
    instructions: data.instructions || undefined,
    notes: data.notes || undefined,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  };
}

export async function listUpcomingScheduledTests(uid: string): Promise<ScheduledTestRecord[]> {
  if (!uid) return [];
  try {
    const snap = await getDocs(
      query(
        collection(db, 'users', uid, 'scheduled_tests'),
        where('status', 'in', OPEN_STATUSES),
        orderBy('scheduledFor', 'asc'),
      ),
    );
    return snap.docs.map((documentSnap) => mapScheduledTest(documentSnap.id, documentSnap.data() as Record<string, any>));
  } catch (error) {
    // Fall back to an un-filtered read if the composite index isn't set up yet.
    try {
      const snap = await getDocs(collection(db, 'users', uid, 'scheduled_tests'));
      return snap.docs
        .map((documentSnap) => mapScheduledTest(documentSnap.id, documentSnap.data() as Record<string, any>))
        .filter((test) => OPEN_STATUSES.includes(test.status));
    } catch {
      console.warn('scheduledTestService: could not read scheduled_tests', error);
      return [];
    }
  }
}

const CATEGORY_LABEL: Record<ScheduledTestCategory, string> = {
  blood_panel: 'Blood panel',
  genetic: 'Genetic',
  imaging: 'Imaging',
  wearable_setup: 'Wearable',
  follow_up: 'Follow-up',
  other: 'Test',
};

export function formatScheduledTestCategory(category: ScheduledTestCategory): string {
  return CATEGORY_LABEL[category] ?? 'Test';
}

const STATUS_LABEL: Record<ScheduledTestStatus, string> = {
  scheduled: 'Scheduled',
  kit_sent: 'Kit on its way',
  in_progress: 'In progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

export function formatScheduledTestStatus(status: ScheduledTestStatus): string {
  return STATUS_LABEL[status] ?? status;
}
